import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, MenuItem, Typography, Alert } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { calculateBMI } from "src/modules/medcard/cards/height-weight/page/create/helpers/bmiCalculator";
import { referenceService } from "src/api/references/referenceService";
import { nurseService } from "src/api/nurse/nurseService";
import dayjs from "dayjs";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { useTranslate } from "src/locales";

interface ServiceAddVkiModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: number;
  onSave?: (data: {
    height: number | null;
    weight: number | null;
    bmi: number | null;
    psychStatus?: number | null;
    maritalStatus?: number | null;
  }) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const ServiceAddVkiModal: React.FC<ServiceAddVkiModalProps> = ({
  open,
  onClose,
  patientId,
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [bmi, setBmi] = useState("");
  const [psychStatus, setPsychStatus] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [psychOptions, setPsychOptions] = useState<{ value: string; label: string }[]>([]);
  const [marriageOptions, setMarriageOptions] = useState<{ value: string; label: string }[]>([]);

  // Hasta bilgilerini yükle
  useEffect(() => {
    if (open && patientId) {
      const loadPatientData = async () => {
        try {
          setLoading(true);
          const patientData: any = await nurseService.patient.getPatientInfo({ id: patientId });
          
          // Genel sağlık bilgilerinden verileri al
          const data = patientData?.data || patientData;
          if (data) {
            if (data.psychological_status) setPsychStatus(String(data.psychological_status));
            if (data.marital_status) setMaritalStatus(String(data.marital_status));
          }
        } catch (err) {
          console.error("Hasta bilgileri yüklenemedi:", err);
        } finally {
          setLoading(false);
        }
      };
      
      loadPatientData();
    }
  }, [open, patientId]);

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        // HEIGHT_INFO form tanımı: Height, Weight, VKI, PsychologicalStatusId, MarriageId
        const res = await referenceService.additionalInfo.getFields({
          formtype: "HEIGHT_INFO",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        setAdditionalInfo(res);

        const fields = res.fields || [];
        const psychField = fields.find(
          (f) => f.fieldname === "PsychologicalStatusId"
        );
        const marriageField = fields.find((f) => f.fieldname === "MarriageId");

        if (psychField && Array.isArray(psychField.options)) {
          setPsychOptions(
            psychField.options.map((o) => ({
              value: String(o.value),
              label: o.label ?? "",
            }))
          );
        } else {
          // Fallback: mevcut sabit liste
          setPsychOptions([
            { value: "sakin", label: "Sakin" },
            { value: "saldirgan", label: "Saldırgan" },
            { value: "depresif", label: "Depresif" },
            { value: "intihara-egilim", label: "İntihara Eğilim" },
            { value: "anksiyoz", label: "Anksiyöz" },
          ]);
        }

        if (marriageField && Array.isArray(marriageField.options)) {
          setMarriageOptions(
            marriageField.options.map((o) => ({
              value: String(o.value),
              label: o.label ?? "",
            }))
          );
        } else {
          setMarriageOptions([
            { value: "evli", label: "Evli" },
            { value: "evli-cocuksuz", label: "Evli çocuksuz" },
            { value: "evli-1-cocuklu", label: "Evli 1 çocuklu" },
            { value: "evli-2-cocuklu", label: "Evli 2 çocuklu" },
            { value: "evli-2den-fazla-cocuklu", label: "Evli >2 çocuklu" },
            { value: "bekar", label: "Bekar" },
            { value: "evli-degil-cocuklu", label: "Evli değil çocuklu" },
          ]);
        }
      } catch {
        // Hata durumunda mevcut sabit seçeneklere düş
        setPsychOptions([
          { value: "sakin", label: t("patient-request.vki.psych.sakin") },
          { value: "saldirgan", label: t("patient-request.vki.psych.saldirgan") },
          { value: "depresif", label: t("patient-request.vki.psych.depresif") },
          { value: "intihara-egilim", label: t("patient-request.vki.psych.intihara-egilim") },
          { value: "anksiyoz", label: t("patient-request.vki.psych.anksiyoz") },
        ]);
        setMarriageOptions([
          { value: "evli", label: t("patient-request.vki.marriage.evli") },
          { value: "evli-cocuksuz", label: t("patient-request.vki.marriage.evli-cocuksuz") },
          { value: "evli-1-cocuklu", label: t("patient-request.vki.marriage.evli-1-cocuklu") },
          { value: "evli-2-cocuklu", label: t("patient-request.vki.marriage.evli-2-cocuklu") },
          {
            value: "evli-2den-fazla-cocuklu",
            label: t("patient-request.vki.marriage.evli-2den-fazla-cocuklu"),
          },
          { value: "bekar", label: t("patient-request.vki.marriage.bekar") },
          {
            value: "evli-degil-cocuklu",
            label: t("patient-request.vki.marriage.evli-degil-cocuklu"),
          },
        ]);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  useEffect(() => {
    const { vki } = calculateBMI(weight, height);
    setBmi(vki || "");
  }, [height, weight]);

  const handleSave = async () => {
    // Validasyon
    if (!height || !weight) {
      setError(t("patient-request.vki.errors.height-weight-required"));
      return;
    }

    if (!patientId) {
      setError(t("patient-request.vki.errors.patient-not-found"));
      return;
    }

    try {
      setSaving(true);
      setError("");

      // VKİ hesapla
      const { vki } = calculateBMI(weight, height);

      // AddPatientHeightWeightTrace çağrılmaz, sadece onSave callback'i çağrılır
      // Veriler serviceAddInfo'da tutulacak ve en sonda AddProcessToPatFileBulk ile gönderilecek
      onSave?.({
        height: height ? parseFloat(height) : null,
        weight: weight ? parseFloat(weight) : null,
        bmi: vki ? parseFloat(vki) : null,
        psychStatus: psychStatus ? Number(psychStatus) : null,
        maritalStatus: maritalStatus ? Number(maritalStatus) : null,
      });
      if (!onSave) {
        onClose();
      }
    } catch (err: any) {
      console.error("Kaydetme hatası:", err);
      setError(err?.message || t("patient-request.vki.errors.save-failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setHeight("");
    setWeight("");
    setBmi("");
    setPsychStatus("");
    setMaritalStatus("");
    setError("");
    onClose();
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.vki.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={saving || loading} />
          <ByzMuiCloseButton onClick={handleClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.vki.height")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiTextField
              fullWidth
              size="small"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              disabled={loading || saving}
              required
              type="number"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.vki.weight")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiTextField
              fullWidth
              size="small"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              disabled={loading || saving}
              required
              type="number"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.vki.bmi")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiTextField 
              fullWidth 
              size="small" 
              value={bmi} 
              disabled 
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.vki.psych-label")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.vki.psych-label")}
              value={
                psychOptions.find((opt) => opt.value === psychStatus)?.label || ""
              }
              selectedId={psychStatus || undefined}
              dialogMaxHeight="60vh"
              staticData={psychOptions.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading || saving}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : [];
                const first = list[0];
                if (first) {
                  setPsychStatus(String(first.id));
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.vki.marriage-label")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.vki.marriage-label")}
              value={
                marriageOptions.find((opt) => opt.value === maritalStatus)?.label ||
                ""
              }
              selectedId={maritalStatus || undefined}
              dialogMaxHeight="60vh"
              staticData={marriageOptions.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading || saving}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setMaritalStatus(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default ServiceAddVkiModal;
